/**
 * Pure translation from a dnd-kit drag-end event's (activeId, overId)
 * strings into a tree mutation — extracted out of LandingBuilder.jsx so it
 * can be unit-tested directly (see dragEndLogic.test.js) without needing
 * to simulate real pointer/DOM drag events.
 */
import { createNode } from './registry/componentRegistry';
import { insertNodeAt, isSelfOrDescendant, moveNodeAfter, findParentContainerId, getChildrenOf } from './treeHelpers';

export const PALETTE_PREFIX = 'palette:';
export const AFTER_PREFIX = 'after:';
export const CONTAINER_PREFIX = 'container:';

/**
 * @param {object[]} nodes - current tree
 * @param {string} activeId - dnd-kit active.id
 * @param {string} overId - dnd-kit over.id
 * @returns {{ type: 'noop' } | { type: 'rejected', reason: string } | { type: 'inserted', nodes: object[], newNodeId: string } | { type: 'moved', nodes: object[] }}
 */
export function applyDragEnd(nodes, activeId, overId) {
    if (activeId === overId) return { type: 'noop' };

    let targetParentId;
    let afterNodeId; // undefined = append; null = prepend; string = insert after that sibling
    if (overId.startsWith(CONTAINER_PREFIX)) {
        targetParentId = overId.slice(CONTAINER_PREFIX.length);
        afterNodeId = undefined;
    } else if (overId.startsWith(AFTER_PREFIX)) {
        const anchorId = overId.slice(AFTER_PREFIX.length);
        const parent = findParentContainerId(nodes, anchorId);
        if (parent === null) return { type: 'noop' }; // anchor no longer exists
        targetParentId = parent;
        afterNodeId = anchorId;
    } else {
        return { type: 'noop' }; // unrecognized drop target
    }

    if (activeId.startsWith(PALETTE_PREFIX)) {
        const type = activeId.slice(PALETTE_PREFIX.length);
        const newNode = createNode(type);
        const insertIndex = afterNodeId === undefined ? undefined : resolveIndexAfterSibling(nodes, targetParentId, afterNodeId);
        return { type: 'inserted', nodes: insertNodeAt(nodes, targetParentId, newNode, insertIndex), newNodeId: newNode.id };
    }

    // Existing canvas/navigator node being moved.
    if (isSelfOrDescendant(nodes, activeId, targetParentId)) {
        return { type: 'rejected', reason: "A component can't be moved inside itself" };
    }
    const moved = moveNodeAfter(nodes, activeId, targetParentId, afterNodeId);
    if (moved === nodes) return { type: 'noop' };
    return { type: 'moved', nodes: moved };
}

// New nodes are never removed-then-reinserted, so the current-tree index
// (unlike moveNodeAfter's post-removal resolution for existing nodes) is
// already correct as-is.
function resolveIndexAfterSibling(nodes, parentId, afterNodeId) {
    const siblings = getChildrenOf(nodes, parentId);
    const idx = siblings.findIndex((n) => n.id === afterNodeId);
    return idx === -1 ? undefined : idx + 1;
}
